import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../../../app/store/Store'
import { IAdOverviewFilter } from './types/AdOverviewFilter.interface'
import { useDateRanges } from '../../../shared/lib/hooks/UseDateRanges'
import { CommaLists, GetAdOverviewAdsApiArg, GetAdOverviewAffiliatesApiArg } from '../../../app/api'
import { IOption } from '../../../shared/components/forms/search-input/BpSearchInput'

export type TAdOverviewTabs = 'Ads' | 'Affiliates'

type AdOverviewState = {
  filters: IAdOverviewFilter
  adsQueryParams: CommaLists<GetAdOverviewAdsApiArg>
  affQueryParams: CommaLists<GetAdOverviewAffiliatesApiArg>
  activeTab: TAdOverviewTabs
  onlyShoppingAds: boolean
  adsExpanded: boolean
  allowDecloak: boolean
  project: IOption & {
    is_demo: boolean
    has_paid_links: boolean
    has_seo_links: boolean
    has_shopping_ads: boolean
  }
}

const dateRanges = useDateRanges()

export const initialAdOverviewFilters = {
  project: '',
  adDomain: '',
  country: '',
  search: '',
  dateRange: dateRanges.month,
  engine: '',
  city: '',
  browser: '',
  policy: '',
  show_hidden: false,
  affiliate: '',
  advertiserName: '',
  is_decloak: false,
  is_top: false,
  group: false,
  reversed_filters: '',
}

const adOverviewSlice = createSlice({
  name: 'overview',
  initialState: {
    filters: initialAdOverviewFilters,
    adsExpanded: false,
    allowDecloak: false,
    project: {
      is_demo: false,
      has_paid_links: true,
      has_seo_links: true,
    },
  } as AdOverviewState,
  reducers: {
    setAdOverviewFilter: (
      state: AdOverviewState,
      { payload }: PayloadAction<IAdOverviewFilter>,
    ) => {
      state.filters = { ...state.filters, ...payload }
    },
    setAdOverviewActiveTab: (
      state: AdOverviewState,
      { payload }: PayloadAction<TAdOverviewTabs>,
    ) => {
      state.activeTab = payload
    },
    setOnlyShoppingAds: (state: AdOverviewState, { payload }: PayloadAction<boolean>) => {
      state.onlyShoppingAds = payload
    },
    setAdsQueryParams: (
      state: AdOverviewState,
      { payload }: PayloadAction<CommaLists<GetAdOverviewAdsApiArg>>,
    ) => {
      state.adsQueryParams = payload
    },
    setAffQueryParams: (
      state: AdOverviewState,
      { payload }: PayloadAction<CommaLists<GetAdOverviewAffiliatesApiArg>>,
    ) => {
      state.affQueryParams = payload
    },
    toggleAdsExpand: (state: AdOverviewState) => {
      state.adsExpanded = !state.adsExpanded
    },
    setAllowDecloak: (state: AdOverviewState, action: PayloadAction<boolean>) => {
      state.allowDecloak = action.payload
    },
    setProject: (
      state: AdOverviewState,
      action: PayloadAction<
        IOption & {
          is_demo: boolean
          has_paid_links: boolean
          has_seo_links: boolean
          has_shopping_ads: boolean
        }
      >,
    ) => {
      state.project = action.payload
    },
  },
})

export const {
  setAdOverviewFilter,
  setAdOverviewActiveTab,
  setOnlyShoppingAds,
  setAdsQueryParams,
  setAffQueryParams,
  toggleAdsExpand,
  setAllowDecloak,
  setProject,
} = adOverviewSlice.actions

export const selectAdOverviewFilters = (state: RootState) => state.root.adOverview.filters
export const selectAdOverviewActiveTab = (state: RootState) => state.root.adOverview.activeTab
export const selectOnlyShoppingAds = (state: RootState) => state.root.adOverview.onlyShoppingAds
export const selectAdOverviewAdsParams = (state: RootState) => state.root.adOverview.adsQueryParams
export const selectAdOverviewAffParams = (state: RootState) => state.root.adOverview.affQueryParams
export const selectAdOverviewAdsExpanded = (state: RootState) => state.root.adOverview.adsExpanded
export const selectAllowDecloak = (state: RootState) => state.root.adOverview.allowDecloak
export const selectProject = (state: RootState) => state.root.adOverview.project

export default adOverviewSlice.reducer
